import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import { AppColors } from "../../../util/AppColors";
import {
  clearPdfBlobCache,
  downloadPdfFile,
  loadPdfBlobUrl,
  normalizeFileUrl,
} from "../../../util/pdfPreview";

const FormsView = ({ forms }) => {
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const [prevModVsble, setPrevModVsble] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState("");
  const pdfPreviewRequestRef = useRef(0);

  const formList = (forms ?? reqDet?.forms)?.filter((frm) => frm?.status);

  useEffect(() => {
    if (!prevModVsble || !selectedForm?.file) {
      setPdfPreviewUrl("");
      setPdfPreviewLoading(false);
      setPdfPreviewError("");
      return undefined;
    }

    const requestId = pdfPreviewRequestRef.current + 1;
    pdfPreviewRequestRef.current = requestId;
    const fileUrl = normalizeFileUrl(selectedForm.file);

    const loadPreview = async () => {
      setPdfPreviewLoading(true);
      setPdfPreviewError("");
      setPdfPreviewUrl("");

      try {
        const blobUrl = await loadPdfBlobUrl(fileUrl);
        if (pdfPreviewRequestRef.current !== requestId) return;
        setPdfPreviewUrl(blobUrl);
      } catch (error) {
        if (pdfPreviewRequestRef.current !== requestId) return;
        setPdfPreviewUrl(fileUrl);
        setPdfPreviewError(
          "Preview could not be loaded inline. Use Open in new tab.",
        );
      } finally {
        if (pdfPreviewRequestRef.current === requestId) {
          setPdfPreviewLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      if (pdfPreviewRequestRef.current === requestId) {
        pdfPreviewRequestRef.current += 1;
      }
    };
  }, [prevModVsble, selectedForm?.file, selectedForm?.petFormId]);

  useEffect(() => () => clearPdfBlobCache(), []);

  const handlePreview = (frm) => {
    if (!normalizeFileUrl(frm?.file)) return;
    setSelectedForm(frm);
    setPrevModVsble(true);
  };

  const onClosePreview = () => {
    pdfPreviewRequestRef.current += 1;
    setPrevModVsble(false);
    setSelectedForm(null);
    setPdfPreviewUrl("");
    setPdfPreviewLoading(false);
    setPdfPreviewError("");
  };

  const openPdfInNewTab = async () => {
    const fileUrl = normalizeFileUrl(selectedForm?.file);
    if (!fileUrl) return;

    try {
      const blobUrl = pdfPreviewUrl || (await loadPdfBlobUrl(fileUrl));
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      <div className="ph20 flex-row flex-row-wrap">
        {formList?.map((frm) => (
          <div
            className="mt10 mr20 flex-column flex-center cursor"
            key={frm?.petFormId}
            onClick={() => handlePreview(frm)}
          >
            <PictureAsPdfIcon
              className="imghw40"
              sx={{ color: AppColors.redBtn }}
            />
            <Typography className="fs12 blue-color txt-regular text-align-center mt5">
              {frm?.name || "Form"}
            </Typography>
          </div>
        ))}
      </div>

      <CustomModal
        open={prevModVsble}
        onClose={onClosePreview}
        header={selectedForm?.name || "Preview"}
        modal
        modalWidth={90}
      >
        <div className="scroll-70vh">
          <div className="flex-center w100Per">
            {pdfPreviewLoading ? (
              <Typography className="txt-regular fs14 gray4 pv20">
                Loading PDF...
              </Typography>
            ) : pdfPreviewUrl ? (
              <object
                key={`${selectedForm?.petFormId}-${pdfPreviewUrl}`}
                data={`${pdfPreviewUrl}#toolbar=1&navpanes=0`}
                type="application/pdf"
                className="pdf-prev"
                style={{ height: "70vh", width: "100%" }}
              >
                <iframe
                  title={selectedForm?.name || "Form preview"}
                  src={`${pdfPreviewUrl}#toolbar=1&navpanes=0`}
                  className="pdf-prev"
                  style={{ height: "70vh", width: "100%", border: 0 }}
                />
              </object>
            ) : (
              <Typography className="txt-regular fs14 red3 pv20">
                {pdfPreviewError || "Unable to load PDF preview."}
              </Typography>
            )}
          </div>
          {pdfPreviewError ? (
            <Typography className="txt-regular fs12 gray4 text-align-center mt10">
              {pdfPreviewError}
            </Typography>
          ) : null}
          <div className="flex-center">
            <div className="mt20 flex-row-ali-cen">
              <CustomButton
                text="Open in new tab"
                onClick={openPdfInNewTab}
                smallBtn
              />
              <div className="ml20">
                <CustomButton
                  text="Download"
                  onClick={() =>
                    downloadPdfFile(
                      selectedForm?.file,
                      `${selectedForm?.name || "form"}.pdf`,
                    )
                  }
                  smallBtn
                />
              </div>
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default FormsView;
